import {
  MP_BUYER_FEE_RATE,
  MP_EARNINGS_PENDING_DAYS,
  MP_REVIEW_HOURS,
  MP_SAFETY_HOLD_DAYS,
  MP_SELLER_FEE_RATE,
} from "./content";
import { PROMOTION_PLANS } from "./promotions";

const sellerFee = `${Math.round(MP_SELLER_FEE_RATE * 100)}%`;
const buyerFee = `${Math.round(MP_BUYER_FEE_RATE * 100)}%`;

export const MP_HIW_INTRO =
  "The BeHot.Now Marketplace is where people hire and get hired. Publishing a service is completely free — you only ever pay a fee when you actually earn, or when you choose to feature your service for extra visibility. Buyers order with Protected Payment, and the money is only released when the delivery is accepted. Everything runs on Heat, the same wallet credit used across the platform.";

const featureLine = PROMOTION_PLANS.map((p) => `${p.days} days for ${p.heat} Heat`).join(", ");

/** Optional paid visibility for an already-published (free) service. */
export const MP_FEATURING = {
  intro:
    "Publishing and keeping a service live costs nothing. Featuring is the optional paid boost: your service is pinned into the Featured rows on the marketplace home, lifted to the top of its category and of search results, and marked with a Featured badge for the number of days you buy.",
  plans: PROMOTION_PLANS.map((p) => ({
    label: p.label,
    heat: p.heat,
    days: p.days,
    blurb: p.blurb,
  })),
  points: [
    "Publishing a service on the marketplace is always free — no listing fee, no subscription.",
    `Featuring is optional and paid in Heat from your wallet: ${featureLine}.`,
    "Featuring only changes placement. It never changes reviews, ratings, seller rank or order protection.",
    "You can feature the same service again at any time; the new days are added on top of the days you still have.",
    "Featured placements are labelled Featured so buyers always know what is paid visibility.",
    "Only live, approved services can be featured. If a service is paused or edited into re-moderation, the countdown keeps running.",
  ],
};

export const MP_BUYER_STEPS: { title: string; body: string }[] = [
  {
    title: "1. Find the right seller",
    body: "Search or browse categories and subcategories — music, video, design, development, influencers, UGC and more. Filter by rating, delivery time and price, and save favourites for later.",
  },
  {
    title: "2. Check the offer",
    body: "Every service page shows packages, what is included, delivery time, revisions, gallery samples, the seller's rank, completed orders, on-time rate and verified reviews.",
  },
  {
    title: "3. Order with Protected Payment",
    body: `You pay in Heat from your wallets — Reward Heat first, then Top-up — plus a ${buyerFee} buyer fee. The funds are held by the platform — the seller never receives them until you accept the delivery.`,
  },
  {
    title: "4. Brief and collaborate",
    body: "After ordering you complete the requirements form and keep everything in the order chat: files, links, revisions and delivery notes are all logged in one place.",
  },
  {
    title: "5. Accept or request a revision",
    body: "When the seller delivers you can accept, request the revisions included in the package, or open a dispute. Accepting releases the payment and unlocks your review.",
  },
];

export const MP_SELLER_STEPS: { title: string; body: string }[] = [
  {
    title: "1. Get verified",
    body: "Every seller completes a one-time identity check — KYC for individuals, KYB for companies (trade licence, company details). Review usually takes up to 5 working days.",
  },
  {
    title: "2. Build your service",
    body: "The step-by-step wizard covers category and subcategory, title and description, tags, up to three packages, gallery media with crop and zoom, and the requirements you need from buyers.",
  },
  {
    title: "3. Pass moderation",
    body: `Each new service — and every edit afterwards — goes to moderation, typically reviewed within ${MP_REVIEW_HOURS} hours. While a service is being re-moderated it is paused and cannot be ordered.`,
  },
  {
    title: "4. Deliver and get paid",
    body: `When the buyer accepts, the platform takes a ${sellerFee} seller fee and the rest lands in your Earn wallet after a ${MP_EARNINGS_PENDING_DAYS}-day pending period.`,
  },
  {
    title: "5. Publish for free",
    body: "Publishing your service costs nothing and there is no subscription. You keep it live as long as you want and only pay a fee on money you actually earn.",
  },
  {
    title: "6. Feature it when you want more eyes",
    body: `Optional paid visibility: ${featureLine}. Featured services appear in the Featured rows on the marketplace home, at the top of their category and of search results, with a Featured badge.`,
  },
];

export const MP_HIW_RULES: string[] = [
  "Publishing a service is free. Heat is only charged for optional featuring and for paid offers.",
  `Featuring is bought per service and per period: ${featureLine}. It is prepaid and non-refundable once it starts.`,
  "Only verified sellers can publish. Unverified accounts can browse, favourite and order but not sell.",
  "All prices are shown in Heat and paid from wallets — Reward Heat from referrals is spent first, then your Top-up balance. Never directly from a card at checkout.",
  `Buyers pay a ${buyerFee} fee on top of the package price; sellers pay a ${sellerFee} fee on the amount earned.`,
  "Protected Payment holds every order. Money moves only on acceptance, dispute resolution or auto-acceptance.",
  `New seller earnings clear after ${MP_EARNINGS_PENDING_DAYS} days, and first-time payouts can sit under a ${MP_SAFETY_HOLD_DAYS}-day safety hold.`,
  "Any edit to a live service pauses selling until it is approved again by moderation.",
  "Reviews can only be left by buyers with a completed order, so ratings reflect real work.",
  "Contact details, off-platform payments and duplicate listings are removed by moderation.",
];

export const MP_HIW_GLOSSARY: [string, string][] = [
  ["Service", "A sellable offer with a title, description, media, tags and up to three packages."],
  ["Package", "One priced tier of a service, with its own scope, delivery time and revision count."],
  ["Protected Payment", "The escrow that holds the buyer's Heat until the delivery is accepted."],
  ["Requirements", "The brief a buyer fills in right after ordering so work can start."],
  ["Revision", "A change round included in the package the buyer purchased."],
  ["Dispute", "A moderated review of an order when buyer and seller cannot agree."],
  ["Earn wallet", "Where cleared seller earnings land, ready for withdrawal."],
  ["Seller rank", "A performance level based on completed orders, ratings and on-time delivery."],
  ["Board listing", "A marketplace service promoted onto a public BeHot.Now chart, boostable with Heat."],
  ["Featuring", "Optional paid placement that pins a live service into the Featured rows and to the top of search for a set number of days."],
  ["Featured badge", "The label shown on any service whose paid featuring window is still running."],
];

export const MP_HIW_FAQ: [string, string][] = [
  [
    "How much does it cost to publish a service?",
    `Nothing. Publishing on the BeHot.Now Marketplace is free of charge — no listing fee and no subscription. The platform only takes a ${sellerFee} seller fee when an order is completed and paid to you.`,
  ],
  [
    "What exactly do I pay Heat for, then?",
    `Only for optional promotion. Featuring a service costs ${featureLine} and simply buys visibility: Featured rows on the marketplace home, top placement in your category and in search, and a Featured badge.`,
  ],
  [
    "What does featuring actually change?",
    "Placement only. Your service is shown earlier and more often to buyers browsing the marketplace. Ratings, reviews, seller rank, delivery rules and Protected Payment are identical for featured and non-featured services.",
  ],
  [
    "Can I extend or stack featuring?",
    "Yes. Buying another plan while a window is still open adds the new days on top of the remaining ones, so your service never falls out of the Featured rows mid-campaign.",
  ],
  [
    "Is featuring refundable?",
    "Featuring is prepaid visibility and starts immediately, so it is not refundable once live. If a technical fault on our side prevents the placement from running we credit the unused days back to your wallet.",
  ],
  [
    "How do buyers pay?",
    `Orders are paid in Heat from your wallets (Reward Heat from referrals counts as internal credit and is spent first, then Top-up), plus a ${buyerFee} buyer fee. If your balance is too low the checkout takes you to top up and completes the order automatically afterwards.`,
  ],
  [
    "Is my money safe before delivery?",
    "Yes. Every order uses Protected Payment: the Heat is held by the platform and is only released to the seller when you accept the delivery or when a dispute is resolved in their favour.",
  ],
  [
    "What happens if the seller does not deliver?",
    "If the delivery deadline passes you can cancel the order and the held Heat is returned to your Top-up wallet. If you disagree about the work, open a dispute and our team reviews the order chat and files.",
  ],
  [
    "How many revisions do I get?",
    "Exactly what the package states. Extra revisions can be agreed with the seller as a package upgrade or a new order.",
  ],
  [
    "Why do I need KYC or KYB verification?",
    "To protect buyers and comply with UAE regulations. Individuals submit identity and address details (KYC); companies submit company name, trade licence and business documents (KYB). Documents can be up to 3 MB each.",
  ],
  [
    "How long does seller verification take?",
    "Usually up to 5 working days. You receive a notification at every status change, and if the team needs more information you can resubmit directly from the seller page.",
  ],
  [
    "When can I withdraw my earnings?",
    `Earnings clear into the Earn wallet ${MP_EARNINGS_PENDING_DAYS} days after the order completes, with a ${MP_SAFETY_HOLD_DAYS}-day safety hold on the earliest payouts. Withdrawals start at US$100 by bank transfer or USDT/USDC (ERC-20), are processed after verification within 48 hours of the request and can take 5 to 8 working days.`,
  ],
  [
    "How long does moderation take?",
    `Most services are reviewed within ${MP_REVIEW_HOURS} hours. Editing a live service sends it back to moderation and pauses sales until it is approved again.`,
  ],
  [
    "Can I promote my service on the charts?",
    "Yes. From your seller dashboard you can list a service on a public board. Pay the listing in Heat, then boost it yourself or let fans boost it to climb the ranking.",
  ],
  [
    "Who can see my seller stats?",
    "Completed orders, average rating and on-time rate are public on your profile and on any board listing connected to your services. Personal documents are only visible to the verification team.",
  ],
  [
    "Can I cancel an order?",
    "Buyer and seller can agree to cancel before delivery and the Heat goes back to the buyer's wallet. After a delivery is accepted the order is final, aside from a dispute.",
  ],
  [
    "Do reviews affect my ranking?",
    "Yes. Ratings, on-time delivery and completed volume feed your seller rank, which affects placement in search, category pages and the HOT sections.",
  ],
  [
    "Can a business sell here?",
    "Yes. Choose the business path during verification (KYB) and provide company name, trade licence and the usual business details. Invoices are issued in AED.",
  ],
  [
    "How do I contact support about an order?",
    "Use the contact form on this page or the order chat. Disputes and refunds are handled by the platform team, never off-platform.",
  ],
];
